import React from "react";
import { IconButton, InputAdornment, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// Icons
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// Your custom components (adjust paths as needed)
import FileView from "./FileView";
import AssignmentFileDropZone from "./AssignmentFileDropZone";
import UserAvatar from "../../Widgets/UserAvatar/UserAvatar";
import TipTapEditor from "../../Widgets/TipTapEditor/TipTapEditor";
import XtramModal, {
    XtramBadge,
    XtramButton,
    XtramCheckbox,
    XtramDatePicker,
    XtramField,
    XtramInput,
    XtramSelect,
    xtramStyles,
} from "../../Widgets/XtramModal/XtramModal";
// Your constants/enums (make sure this path is correct)
import Constants from "../../../assests/Constants/constants";
import { priorityKey, statusKey } from "./assignmentDisplay";
import styles from "./AssignmentDialog.module.scss";

dayjs.extend(utc);

const HOURS_PER_DAY = 8;

// Assignment dates are stored as UTC midnight; the picker works with the local calendar day.
const utcDayToLocal = (value) => {
    if (!value) return null;
    const day = dayjs.utc(value);
    return day.isValid() ? new Date(day.year(), day.month(), day.date()) : null;
};
const localDayToUtcIso = (date) =>
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();

/**
 * Props:
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Function to call when dialog should close
 * @param {boolean} isEditMode - If true, dialog is in "Edit" mode
 * @param {boolean} isAddMode - If true, indicates this is a new assignment (affects UI for deleting)
 * @param {boolean} isViewOnly - If true, the edit action is hidden
 * @param {Object} currentAssignment - The assignment data object
 * @param {function} setCurrentAssignment - Setter for updating assignment
 * @param {Array} subAccountants - Array of sub-accountant objects
 * @param {Array} categoryOptions - Array of category objects {value, label}
 * @param {Array} caseIdOptions - Array of client/case objects {value, label, [deductionsCase]}
 * @param {function} handleSaveAssignment - Called when user clicks "Save"
 * @param {function} handleEditAssignment - Called when user clicks "Edit"
 * @param {function} handleCloseDialog - Called to close the dialog (often same as onClose)
 * @param {function} handleOpenModal - Opens the file preview
 * @param {function} handleOpenNewCategoryModal - Called if user picks "NEW_CATEGORY"
 * @param {function} changeOnAssignmentCategory - Updates the assignment's category
 * @param {function} changeOnAssignment - Updates the assignment's client
 * @param {function} handleAddFilesToAssignment - Adds files to the assignment
 * @param {function} handleRemoveDocumentFromAssignment - Remove attached doc from assignment
 * @param {function} handleAddFilesToComment - Adds files to the new comment
 * @param {function} handleRemoveDocumentFromComment - Remove attached doc from comment
 * @param {Object} fileInputRefUpload - Ref to file input for assignment
 * @param {Object} fileInputRefComment - Ref to file input for comment
 * @param {function} handleChangeCurrentDescription - Handler for assignment description editor
 * @param {function} handleChangeNewCommentContent - Handler for new comment editor
 * @param {Object} newComment - Current new-comment object { content, attachedDocuments, etc. }
 * @param {function} handleAddComment - Adds the new comment to assignment
 * @param {function} handleDeleteComment - Deletes an existing comment
 * @param {function} formatDate - Utility for formatting date
 * @param {function} formatDateWithHour - Utility for formatting date/time
 * @param {Object} user - The current user object
 * @param {function} setIsConfirmModalVisable - Opens the "confirm delete" modal
 * @param {function} t - The i18n translation function
 * @param {string} direction - 'rtl' | 'ltr'
 */
const AssignmentDialog = ({
    open,
    onClose,

    /* Modes/States */
    isEditMode,
    isAddMode,
    isViewOnly = false,

    /* Assignment Data */
    currentAssignment,
    setCurrentAssignment,

    /* Arrays/Data */
    subAccountants = [],
    categoryOptions = [],
    caseIdOptions = [],

    /* Parent Handlers */
    handleSaveAssignment,
    handleEditAssignment,
    handleCloseDialog,
    handleOpenModal,
    handleOpenNewCategoryModal,
    changeOnAssignmentCategory,
    changeOnAssignment,
    setIsConfirmModalVisable,

    /* File & Comment Handlers */
    handleAddFilesToAssignment,
    handleRemoveDocumentFromAssignment,
    handleAddFilesToComment,
    handleRemoveDocumentFromComment,
    fileInputRefUpload,
    fileInputRefComment,

    /* Description & Comments */
    handleChangeCurrentDescription,
    handleChangeNewCommentContent,
    newComment,
    handleAddComment,
    handleDeleteComment,

    /* Helpers */
    formatDate,
    formatDateWithHour,

    /* Auth/User Data */
    user,

    /* i18n */
    t,
    direction = "rtl",
}) => {
    const updateAssignment = (patch) => setCurrentAssignment({ ...currentAssignment, ...patch });

    const accountantOptions = subAccountants.map((acc) => ({ value: acc.subAccountantId, label: acc.name }));
    const assigneeOptions = [{ value: "", label: t("NoAssignee") }, ...accountantOptions];
    const clientOptions = caseIdOptions.map((option) => ({
        value: option.value,
        label: option.value ? `${option.label} - ${option.value}` : option.label,
    }));
    const statusOptions = [
        { value: Constants.StatusEnum.ToDo, label: t("ToDo") },
        { value: Constants.StatusEnum.InProgress, label: t("InProgress") },
        { value: Constants.StatusEnum.Done, label: t("Done") },
    ];
    const priorityOptions = [
        { value: Constants.PriorityEnum.Low, label: t("Low") },
        { value: Constants.PriorityEnum.High, label: t("High") },
        { value: Constants.PriorityEnum.Critical, label: t("Critical") },
    ];

    const estimatedTime = currentAssignment?.estimatedTime || 1;
    const estimatedDays = Math.floor(estimatedTime / HOURS_PER_DAY);
    const estimatedHours = estimatedTime % HOURS_PER_DAY;

    const assigneeName = subAccountants.find((acc) => acc.subAccountantId === currentAssignment?.assigneeId)?.name
        || t("NoAssignee");
    const reporterName = subAccountants.find((acc) => acc.subAccountantId === currentAssignment?.reporterId)?.name
        || currentAssignment?.reporterId
        || t("NoClient");
    const client = caseIdOptions.find((option) => option.value === currentAssignment?.caseId);
    const categoryLabel = categoryOptions.find((option) => option.value === currentAssignment?.category)?.label
        || t("NoCategory");
    const statusLabel = t(statusKey(currentAssignment?.status) || "");
    const priorityLabel = t(priorityKey(currentAssignment?.priority) || "");

    const canDeleteComment = (comment) =>
        (!user?.subAccountantId && user?.accountantId === comment.commentorId)
        || user?.subAccountantId === comment.commentorId;

    const closeFromFooter = handleCloseDialog || onClose;

    const title = (
        <span className={styles.titleRow}>
            {isAddMode
                ? t("AddAssignment")
                : isEditMode
                    ? t("Edit")
                    : `${t("Assignment")}: ${currentAssignment?.summary || ""}`}
            {currentAssignment?.isPrivate && (
                <Tooltip title={t("PrivateAssignmentTooltip")}>
                    <span>
                        <XtramBadge>
                            <LockOutlinedIcon sx={{ fontSize: 13, marginInlineEnd: "4px" }} />
                            {t("PrivateAssignment")}
                        </XtramBadge>
                    </span>
                </Tooltip>
            )}
        </span>
    );

    const renderDetail = (label, value) => (
        <div className={styles.detail}>
            <span className={styles.detailLabel}>{label}</span>
            <div className={styles.detailValue}>{value}</div>
        </div>
    );

    const renderEditFields = () => (
        <>
            <div className={xtramStyles.section}>
                <XtramField label={t("AssignmentTitle")} required size="full">
                    <XtramInput
                        direction={direction}
                        value={currentAssignment?.summary || ""}
                        onChange={(e) => updateAssignment({ summary: e.target.value })}
                        error={!currentAssignment?.summary}
                    />
                </XtramField>

                <div className={xtramStyles.formFields}>
                    <XtramField label={t("Assignee")}>
                        <XtramSelect
                            direction={direction}
                            options={assigneeOptions}
                            value={assigneeOptions.find((o) => o.value === (currentAssignment?.assigneeId || "")) || null}
                            placeholder={t("SelectAssignee")}
                            onChange={(option) => {
                                const assigneeId = option?.value || "";
                                const assigneeType = !assigneeId
                                    ? null
                                    : assigneeId === user?.accountantId
                                        ? Constants.UserTypeInt.Accountant
                                        : Constants.UserTypeInt.SubAccountant;
                                updateAssignment({ assigneeId: assigneeId || null, assigneeType });
                            }}
                        />
                    </XtramField>
                    <XtramField label={t("Reporter")}>
                        <XtramSelect
                            direction={direction}
                            options={accountantOptions}
                            value={accountantOptions.find((o) => o.value === currentAssignment?.reporterId) || null}
                            placeholder={t("SelectReporter")}
                            onChange={(option) => updateAssignment({ reporterId: option?.value || "" })}
                        />
                    </XtramField>
                    <XtramField label={t("Client Name")}>
                        <XtramSelect
                            direction={direction}
                            options={clientOptions}
                            value={clientOptions.find((o) => o.value === currentAssignment?.caseId) || null}
                            placeholder={t("SelectClient")}
                            onChange={(option) => changeOnAssignment(option ?? { value: null })}
                        />
                    </XtramField>
                    <XtramField label={t("Status")} required>
                        <XtramSelect
                            direction={direction}
                            options={statusOptions}
                            value={statusOptions.find((o) => o.value === currentAssignment?.status) || null}
                            placeholder={t("SelectStatus")}
                            onChange={(option) => option && updateAssignment({ status: option.value })}
                        />
                    </XtramField>
                    <XtramField label={t("Priority")} required>
                        <XtramSelect
                            direction={direction}
                            options={priorityOptions}
                            value={priorityOptions.find((o) => o.value === currentAssignment?.priority) || null}
                            placeholder={t("SelectPriority")}
                            onChange={(option) => option && updateAssignment({ priority: option.value })}
                            error={currentAssignment?.priority === null || currentAssignment?.priority === undefined}
                        />
                    </XtramField>
                    <XtramField label={t("Category")}>
                        <XtramSelect
                            direction={direction}
                            options={categoryOptions}
                            value={categoryOptions.find((o) => o.value === currentAssignment?.category) || null}
                            placeholder={t("SelectCategory")}
                            onChange={(option) => {
                                if (option?.value === "NEW_CATEGORY") handleOpenNewCategoryModal();
                                changeOnAssignmentCategory(option ?? { value: null });
                            }}
                        />
                    </XtramField>
                    <XtramField label={t("CreationDate")}>
                        <XtramDatePicker
                            value={utcDayToLocal(currentAssignment?.createDate)}
                            onChange={(date) => date && updateAssignment({ createDate: localDayToUtcIso(date) })}
                        />
                    </XtramField>
                    <XtramField label={t("DueDate")}>
                        <XtramDatePicker
                            value={utcDayToLocal(currentAssignment?.dueDate)}
                            onChange={(date) => date && updateAssignment({ dueDate: localDayToUtcIso(date) })}
                        />
                    </XtramField>
                    <XtramField label={t("EstimatedTime")}>
                        <div className={styles.estimate}>
                            <XtramInput
                                direction={direction}
                                type="number"
                                value={estimatedDays}
                                inputProps={{ min: 0, max: 125 }}
                                InputProps={{ endAdornment: <InputAdornment position="end">{t("Days")}</InputAdornment> }}
                                onChange={(e) => {
                                    const days = parseInt(e.target.value, 10) || 0;
                                    updateAssignment({ estimatedTime: days * HOURS_PER_DAY + estimatedHours });
                                }}
                            />
                            <XtramInput
                                direction={direction}
                                type="number"
                                value={estimatedHours}
                                inputProps={{ min: 0, max: HOURS_PER_DAY - 1 }}
                                InputProps={{ endAdornment: <InputAdornment position="end">{t("Hours")}</InputAdornment> }}
                                onChange={(e) => {
                                    const hours = parseInt(e.target.value, 10) || 0;
                                    updateAssignment({ estimatedTime: estimatedDays * HOURS_PER_DAY + hours });
                                }}
                            />
                        </div>
                    </XtramField>
                </div>

                <div className={styles.switches}>
                    <XtramCheckbox
                        checked={currentAssignment?.isPrivate}
                        onChange={(checked) => updateAssignment({ isPrivate: checked })}
                        label={t("PrivateAssignment")}
                    />
                    <XtramCheckbox
                        checked={currentAssignment?.notifyAssignee}
                        disabled={!currentAssignment?.assigneeId}
                        onChange={(checked) => updateAssignment({ notifyAssignee: checked })}
                        label={t("NotifyAssigneeInChat")}
                    />
                </div>
            </div>

            <div className={xtramStyles.section}>
                <h3 className={xtramStyles.sectionTitle}>{t("Description")}</h3>
                <TipTapEditor
                    value={currentAssignment?.description || ""}
                    onChange={handleChangeCurrentDescription}
                    placeholder={t("TypeYourCommentHere") || "Type your comment here..."}
                    direction={direction}
                />
                <AssignmentFileDropZone
                    onFilesAdded={handleAddFilesToAssignment}
                    fileInputRef={fileInputRefUpload}
                    attachedFiles={currentAssignment?.attachedDocuments || []}
                    onRemoveFile={handleRemoveDocumentFromAssignment}
                    onOpenModal={handleOpenModal}
                    t={t}
                />
            </div>
        </>
    );

    const renderDetails = () => (
        <>
            <div className={xtramStyles.section}>
                <div className={styles.details}>
                    {renderDetail(t("Assignee"), <UserAvatar name={assigneeName} flip size={26} />)}
                    {renderDetail(t("Reporter"), <UserAvatar name={reporterName} flip size={26} />)}
                    {renderDetail(t("Client Name"), (
                        <UserAvatar
                            name={client?.label || t("NoClient")}
                            flip
                            size={26}
                            caseId={client?.value || null}
                            deduction={client?.deductionsCase || null}
                        />
                    ))}
                    {renderDetail(t("Status"), statusLabel)}
                    {renderDetail(t("Priority"), priorityLabel)}
                    {renderDetail(t("Category"), categoryLabel)}
                    {renderDetail(t("CreationDate"), formatDateWithHour(currentAssignment?.createDate) || "")}
                    {renderDetail(t("DueDate"), formatDate(currentAssignment?.dueDate) || "")}
                    {renderDetail(
                        t("EstimatedTime"),
                        `${estimatedDays} ${t("Days")} · ${estimatedHours} ${t("Hours")}`
                    )}
                </div>
                {currentAssignment?.isPrivate && (
                    <p className={xtramStyles.muted}>{t("PrivateAssignmentTooltip")}</p>
                )}
            </div>

            <div className={xtramStyles.section}>
                <h3 className={xtramStyles.sectionTitle}>{t("Description")}</h3>
                <TipTapEditor
                    value={currentAssignment?.description || ""}
                    onChange={() => {}}
                    placeholder={t("TypeYourCommentHere") || "Type your comment here..."}
                    direction={direction}
                    editable={false}
                />
                {currentAssignment?.attachedDocuments?.length ? (
                    <div className={styles.files}>
                        {currentAssignment.attachedDocuments.map((file, index) => (
                            <FileView
                                key={index}
                                file={file}
                                onRemove={() => handleRemoveDocumentFromAssignment(index)}
                                isCreate={false}
                                onOpenModal={handleOpenModal}
                            />
                        ))}
                    </div>
                ) : null}
            </div>

            <div className={xtramStyles.section}>
                <h3 className={xtramStyles.sectionTitle}>{t("Comments")}</h3>
                <TipTapEditor
                    value={newComment?.content || ""}
                    onChange={handleChangeNewCommentContent}
                    placeholder={t("TypeYourCommentHere") || "Type your comment here..."}
                    direction={direction}
                />
                <AssignmentFileDropZone
                    onFilesAdded={handleAddFilesToComment}
                    fileInputRef={fileInputRefComment}
                    attachedFiles={newComment?.attachedDocuments || []}
                    onRemoveFile={handleRemoveDocumentFromComment}
                    onOpenModal={handleOpenModal}
                    t={t}
                />
                <div className={styles.commentActions}>
                    <XtramButton variant="secondary" small onClick={() => handleAddComment(currentAssignment.id)}>
                        {t("AddComment")}
                    </XtramButton>
                </div>

                {currentAssignment?.comments?.map((comment, index) => (
                    <div key={comment.id || index} className={xtramStyles.card}>
                        <div className={styles.commentHeader}>
                            <div className={styles.commentAuthor}>
                                <UserAvatar name={comment.commentorName} flip withName={false} />
                                <span>{comment.commentorName}</span>
                                <span className={styles.commentDate}>{formatDateWithHour(comment.commentDate)}</span>
                            </div>
                            {canDeleteComment(comment) && (
                                <Tooltip title={t("Delete")}>
                                    <IconButton size="small" onClick={() => handleDeleteComment(comment.id)}>
                                        <DeleteIcon fontSize="small" className={styles.deleteIcon} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>

                        <div
                            className={styles.commentBody}
                            style={{ direction: comment.content?.includes("ql-direction-rtl") ? "rtl" : "ltr" }}
                            dangerouslySetInnerHTML={{ __html: comment.content }}
                        />

                        {comment?.attachedDocuments?.length ? (
                            <div className={styles.commentFiles}>
                                {comment.attachedDocuments.map((file, idx) => (
                                    <FileView
                                        key={idx}
                                        file={file}
                                        isCreate={false}
                                        onRemove={() => handleRemoveDocumentFromComment(idx)}
                                        onOpenModal={handleOpenModal}
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
        </>
    );

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            direction={direction}
            wide
            title={title}
            footer={(
                <>
                    <XtramButton variant="secondary" onClick={closeFromFooter}>
                        {t("Close")}
                    </XtramButton>
                    {isEditMode && !isAddMode && (
                        <XtramButton
                            variant="ghost"
                            className={xtramStyles.textError}
                            onClick={() => setIsConfirmModalVisable(true)}
                        >
                            <DeleteIcon fontSize="small" />
                            {t("Delete")}
                        </XtramButton>
                    )}
                    {isEditMode ? (
                        <XtramButton variant="primary" onClick={handleSaveAssignment}>
                            <SaveIcon fontSize="small" />
                            {t("Save")}
                        </XtramButton>
                    ) : !isViewOnly && (
                        <XtramButton variant="primary" onClick={handleEditAssignment}>
                            <EditIcon fontSize="small" />
                            {t("Edit")}
                        </XtramButton>
                    )}
                </>
            )}
        >
            {isEditMode ? renderEditFields() : renderDetails()}
        </XtramModal>
    );
};

export default AssignmentDialog;
